import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FileCheck, Shield, Globe, X, ZoomIn, Award, FileText, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InstitutionalValidations() {
  const { language } = useLanguage();
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/track/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: 'validations', language })
    }).catch(() => {});
  }, [language]);

  const trackDocumentClick = (docName) => {
    fetch(`${API_URL}/api/track/document-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: 'validations', document_clicked: docName, language })
    }).catch(() => {});
  };

  const content = {
    fr: {
      badge: "Le Mur de Preuves",
      title: "Nos Validations Institutionnelles",
      subtitle: "Chaque document est une preuve officielle de la légitimité et de la crédibilité de LENODA. Cliquez pour consulter les documents officiels.",
      enlargeBtn: "Agrandir le document officiel",
      categories: {
        ministerial: "Validations Ministérielles",
        intellectual: "Propriété Intellectuelle",
        interest: "Lettres d'Intérêt"
      }
    },
    en: {
      badge: "The Wall of Proof",
      title: "Our Institutional Validations",
      subtitle: "Each document is official proof of LENODA's legitimacy and credibility. Click to view official documents.",
      enlargeBtn: "Enlarge official document",
      categories: {
        ministerial: "Ministerial Validations",
        intellectual: "Intellectual Property",
        interest: "Letters of Interest"
      }
    },
    pt: {
      badge: "O Muro de Provas",
      title: "Nossas Validações Institucionais",
      subtitle: "Cada documento é uma prova oficial da legitimidade e credibilidade da LENODA. Clique para ver os documentos oficiais.",
      enlargeBtn: "Ampliar documento oficial",
      categories: {
        ministerial: "Validações Ministeriais",
        intellectual: "Propriedade Intelectual",
        interest: "Cartas de Interesse"
      }
    },
    it: {
      badge: "Il Muro delle Prove",
      title: "Le Nostre Validazioni Istituzionali",
      subtitle: "Ogni documento è una prova ufficiale della legittimità e credibilità di LENODA. Clicca per visualizzare i documenti ufficiali.",
      enlargeBtn: "Ingrandire il documento ufficiale",
      categories: {
        ministerial: "Validazioni Ministeriali",
        intellectual: "Proprietà Intellettuale",
        interest: "Lettere di Interesse"
      }
    },
    ru: {
      badge: "Стена Доказательств",
      title: "Наши Институциональные Валидации",
      subtitle: "Каждый документ является официальным доказательством легитимности и достоверности LENODA. Нажмите для просмотра официальных документов.",
      enlargeBtn: "Увеличить официальный документ",
      categories: {
        ministerial: "Министерские Валидации",
        intellectual: "Интеллектуальная Собственность",
        interest: "Письма об Интересе"
      }
    },
    zh: {
      badge: "证明墙",
      title: "我们的机构认证",
      subtitle: "每份文件都是LENODA合法性和信誉的官方证明。点击查看官方文件。",
      enlargeBtn: "放大官方文件",
      categories: {
        ministerial: "部级认证",
        intellectual: "知识产权",
        interest: "意向函"
      }
    }
  };

  const documents = {
    ministerial: [
      {
        id: 'minedub',
        image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/n4gaq5o9_courrier%20du%20MINEDUB.png',
        title: {
          fr: "Validation MINEDUB",
          en: "MINEDUB Validation",
          pt: "Validação MINEDUB",
          it: "Validazione MINEDUB",
          ru: "Валидация MINEDUB",
          zh: "MINEDUB认证"
        },
        description: {
          fr: "Lettre officielle du Ministère de l'Éducation de Base du Cameroun approuvant le Programme de Réarmement Patriotique.",
          en: "Official letter from the Ministry of Basic Education of Cameroon approving the Patriotic Rearmament Program.",
          pt: "Carta oficial do Ministério da Educação Básica dos Camarões aprovando o Programa de Rearmamento Patriótico.",
          it: "Lettera ufficiale del Ministero dell'Istruzione di Base del Camerun che approva il Programma di Riarmo Patriottico.",
          ru: "Официальное письмо Министерства базового образования Камеруна, утверждающее Программу патриотического перевооружения.",
          zh: "喀麦隆基础教育部关于批准爱国重整计划的官方函件。"
        },
        date: {
          fr: "01 Mars 2025", en: "01 March 2025", pt: "01 de Março de 2025",
          it: "01 Marzo 2025", ru: "01 марта 2025", zh: "2025年3月1日"
        }
      }
    ],
    intellectual: [
      {
        id: 'inpi',
        image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/127cz80b_INPI.png',
        title: {
          fr: "Certificat INPI",
          en: "INPI Certificate",
          pt: "Certificado INPI",
          it: "Certificato INPI",
          ru: "Сертификат INPI",
          zh: "INPI证书"
        },
        description: {
          fr: "Certificat de protection de la propriété intellectuelle délivré par l'Institut National de la Propriété Industrielle de France. N° DSO2026004704.",
          en: "Intellectual property protection certificate issued by the National Institute of Industrial Property of France. N° DSO2026004704.",
          pt: "Certificado de proteção de propriedade intelectual emitido pelo Instituto Nacional de Propriedade Industrial de França. N° DSO2026004704.",
          it: "Certificato di protezione della proprietà intellettuale rilasciato dall'Istituto Nazionale della Proprietà Industriale di Francia. N° DSO2026004704.",
          ru: "Сертификат защиты интеллектуальной собственности, выданный Национальным институтом промышленной собственности Франции. № DSO2026004704.",
          zh: "法国国家工业产权局颁发的知识产权保护证书。编号 DSO2026004704。"
        },
        date: { fr: "2024", en: "2024", pt: "2024", it: "2024", ru: "2024", zh: "2024" }
      }
    ],
    interest: [
      {
        id: 'guinea-bissau',
        image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/vr885rcm_int%C3%A9r%C3%AAt%20Guin%C3%A9e%20Bissau.png',
        title: {
          fr: "Guinée-Bissau", en: "Guinea-Bissau", pt: "Guiné-Bissau",
          it: "Guinea-Bissau", ru: "Гвинея-Бисау", zh: "几内亚比绍"
        },
        description: {
          fr: "Ministère de la Santé Publique",
          en: "Ministry of Public Health",
          pt: "Ministério da Saúde Pública",
          it: "Ministero della Salute Pubblica",
          ru: "Министерство общественного здравоохранения",
          zh: "公共卫生部"
        },
        date: {
          fr: "22 Mai 2024", en: "22 May 2024", pt: "22 Maio 2024",
          it: "22 Maggio 2024", ru: "22 мая 2024", zh: "2024年5月22日"
        },
        flag: "🇬🇼"
      },
      {
        id: 'nigeria-abuja',
        image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/2cbjvzy6_int%C3%A9r%C3%AAt%20Abuja%20Nig%C3%A9ria.png',
        title: {
          fr: "Nigéria - Abuja", en: "Nigeria - Abuja", pt: "Nigéria - Abuja",
          it: "Nigeria - Abuja", ru: "Нигерия - Абуджа", zh: "尼日利亚 - 阿布贾"
        },
        description: {
          fr: "Ministère Fédéral de la Santé",
          en: "Federal Ministry of Health",
          pt: "Ministério Federal da Saúde",
          it: "Ministero Federale della Salute",
          ru: "Федеральное министерство здравоохранения",
          zh: "联邦卫生部"
        },
        date: {
          fr: "11 Juin 2024", en: "11 June 2024", pt: "11 Junho 2024",
          it: "11 Giugno 2024", ru: "11 июня 2024", zh: "2024年6月11日"
        },
        flag: "🇳🇬"
      },
      {
        id: 'cameroon-west',
        image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/x4wwo05m_int%C3%A9r%C3%AAt%20Oest%20Cameroun.png',
        title: {
          fr: "Cameroun - Ouest", en: "Cameroon - West", pt: "Camarões - Oeste",
          it: "Camerun - Ovest", ru: "Камерун - Запад", zh: "喀麦隆 - 西部"
        },
        description: {
          fr: "Conseil Régional (4 CHRu)",
          en: "Regional Council (4 CHRu)",
          pt: "Conselho Regional (4 CHRu)",
          it: "Consiglio Regionale (4 CHRu)",
          ru: "Региональный совет (4 CHRu)",
          zh: "地区委员会（4个CHRu）"
        },
        date: { fr: "2024", en: "2024", pt: "2024", it: "2024", ru: "2024", zh: "2024" },
        flag: "🇨🇲"
      },
      {
        id: 'cameroon-east',
        image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/quackqyj_int%C3%A9r%C3%AAt%20Est%20Cameroun.png',
        title: {
          fr: "Cameroun - Est", en: "Cameroon - East", pt: "Camarões - Leste",
          it: "Camerun - Est", ru: "Камерун - Восток", zh: "喀麦隆 - 东部"
        },
        description: {
          fr: "Conseil Régional",
          en: "Regional Council",
          pt: "Conselho Regional",
          it: "Consiglio Regionale",
          ru: "Региональный совет",
          zh: "地区委员会"
        },
        date: {
          fr: "23 Mai 2024", en: "23 May 2024", pt: "23 Maio 2024",
          it: "23 Maggio 2024", ru: "23 мая 2024", zh: "2024年5月23日"
        },
        flag: "🇨🇲"
      },
      {
        id: 'cameroon-littoral',
        image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/rv9pev1w_int%C3%A9r%C3%AAt%20Littoral%20Cameroun.png',
        title: {
          fr: "Cameroun - Littoral", en: "Cameroon - Littoral", pt: "Camarões - Litoral",
          it: "Camerun - Litorale", ru: "Камерун - Литораль", zh: "喀麦隆 - 滨海"
        },
        description: {
          fr: "Conseil Régional (34+4 Hôpitaux)",
          en: "Regional Council (34+4 Hospitals)",
          pt: "Conselho Regional (34+4 Hospitais)",
          it: "Consiglio Regionale (34+4 Ospedali)",
          ru: "Региональный совет (34+4 больницы)",
          zh: "地区委员会（34+4家医院）"
        },
        date: {
          fr: "06 Fév 2024", en: "06 Feb 2024", pt: "06 Fev 2024",
          it: "06 Feb 2024", ru: "06 февраля 2024", zh: "2024年2月6日"
        },
        flag: "🇨🇲"
      },
      {
        id: 'cameroon-sch',
        image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/eoktf9y4_int%C3%A9r%C3%AAt%20sch%20Cameroun.png',
        title: {
          fr: "Cameroun - SCH", en: "Cameroon - SCH", pt: "Camarões - SCH",
          it: "Camerun - SCH", ru: "Камерун - SCH", zh: "喀麦隆 - SCH"
        },
        description: {
          fr: "Centre Scientifique de Santé",
          en: "Scientific Center for Health",
          pt: "Centro Científico de Saúde",
          it: "Centro Scientifico di Salute",
          ru: "Научный центр здравоохранения",
          zh: "卫生科学中心"
        },
        date: {
          fr: "04 Juin 2024", en: "04 June 2024", pt: "04 Junho 2024",
          it: "04 Giugno 2024", ru: "04 июня 2024", zh: "2024年6月4日"
        },
        flag: "🇨🇲"
      }
    ]
  };

  const c = content[language] || content.fr;

  const handleDocClick = (doc) => {
    trackDocumentClick(doc.id);
    setSelectedDoc(doc);
  };

  const renderDocCard = (doc, icon) => (
    <div 
      key={doc.id}
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:border-emerald-300 transition-all cursor-pointer group"
      onClick={() => handleDocClick(doc)}
      data-testid={`doc-card-${doc.id}`}
    >
      <div className="relative h-48 bg-slate-100 overflow-hidden">
        <img 
          src={doc.image} 
          alt={doc.title[language] || doc.title.fr}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-4 py-2 rounded-full flex items-center gap-2">
            <ZoomIn className="w-4 h-4 text-slate-700" />
            <span className="text-sm font-medium text-slate-700">{c.enlargeBtn}</span>
          </div>
        </div>
        {doc.flag && (
          <div className="absolute top-3 left-3 text-3xl">{doc.flag}</div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          {icon}
          <div>
            <h3 className="text-lg font-bold text-slate-900">{doc.title[language] || doc.title.fr}</h3>
            <p className="text-xs text-emerald-600 font-semibold">{doc.date[language] || doc.date.fr}</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 line-clamp-2">{doc.description[language] || doc.description.fr}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="validations-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-30"></div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-emerald-600/20 border border-emerald-400/30 rounded-full backdrop-blur-sm mb-6">
            <p className="text-sm font-medium text-emerald-300 uppercase tracking-wider flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" />
              {c.badge}
            </p>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            {c.title}
          </h1>
          
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto">{c.subtitle}</p>
        </div>
      </section>

      {/* Ministerial Validations */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
              {c.categories.ministerial}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.ministerial.map(doc => renderDocCard(doc, <FileCheck className="w-6 h-6 text-amber-600 flex-shrink-0" />))}
          </div>
        </div>
      </section>

      {/* Intellectual Property */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-sky-100 rounded-2xl flex items-center justify-center">
              <Award className="w-6 h-6 text-sky-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
              {c.categories.intellectual}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.intellectual.map(doc => renderDocCard(doc, <Shield className="w-6 h-6 text-sky-600 flex-shrink-0" />))}
          </div>
        </div>
      </section>

      {/* Letters of Interest */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
              {c.categories.interest}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.interest.map(doc => renderDocCard(doc, <FileText className="w-6 h-6 text-emerald-600 flex-shrink-0" />))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedDoc && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedDoc(null)}
          data-testid="doc-lightbox"
        >
          <button 
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            onClick={() => setSelectedDoc(null)}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-4xl w-full">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 mb-4">
              <div className="flex items-center gap-4">
                {selectedDoc.flag && <div className="text-4xl">{selectedDoc.flag}</div>}
                <div>
                  <h3 className="text-2xl font-bold text-white">{selectedDoc.title[language] || selectedDoc.title.fr}</h3>
                  <p className="text-slate-300">{selectedDoc.description[language] || selectedDoc.description.fr}</p>
                  <p className="text-sm text-emerald-400 font-semibold mt-1">{selectedDoc.date[language] || selectedDoc.date.fr}</p>
                </div>
              </div>
            </div>
            <img 
              src={selectedDoc.image}
              alt={selectedDoc.title[language] || selectedDoc.title.fr}
              className="w-full rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
